import { useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
    Divider,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography
} from '@mui/material';
import { ConfirmDialog } from '../../../shared/ConfirmDialog';
import { TextWithMentions } from './TextWithMentions';
import { DisplayComment } from './useFollowUpActionComments';

const htmlDecode = (input: string) => {
    const doc = new DOMParser().parseFromString(input, 'text/html');
    return doc.documentElement.textContent;
};

interface FollowUpActionCommentItemProps {
    comment: DisplayComment;
    isPendingDelete: boolean;
    showDivider: boolean;
    onRequestDelete: (comment: DisplayComment) => void;
    onCancelDelete: () => void;
    onConfirmDelete: () => void;
}

export const FollowUpActionCommentItem = (props: FollowUpActionCommentItemProps) => {
    const { comment, isPendingDelete, showDivider, onRequestDelete, onCancelDelete, onConfirmDelete } = props;

    const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

    const handleCloseMenu = () => setMenuAnchorEl(null);

    const handleDeleteComment = () => {
        onRequestDelete(comment);
        handleCloseMenu();
    };

    return (
        <>
            <ListItem
                alignItems="flex-start"
                disableGutters
                secondaryAction={
                    comment.canDelete && (
                        <IconButton
                            edge="end"
                            size="small"
                            color="default"
                            aria-label="More options"
                            onClick={(event) => setMenuAnchorEl(event.currentTarget)}
                        >
                            <MoreVertIcon fontSize="small" />
                        </IconButton>
                    )
                }
            >
                <ListItemText
                    primary={
                        <>
                            {comment.isActive && (
                                <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                                    <Stack direction="row" alignItems="baseline" gap={2}>
                                        <Typography variant="subtitle2" noWrap sx={{ maxWidth: 160 }}>
                                            {comment.displayName}
                                        </Typography>
                                        {comment.userRole && (
                                            <Typography variant="caption" color="text.disabled">
                                                {comment.userRole}
                                            </Typography>
                                        )}
                                    </Stack>
                                </Stack>
                            )}
                        </>
                    }
                    secondary={
                        <>
                            {comment.isActive ? (
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'black' }}>
                                    {comment.message && (
                                        <TextWithMentions
                                            message={htmlDecode(comment.message)}
                                            mentions={comment.mentions}
                                        />
                                    )}
                                </Typography>
                            ) : (
                                <Stack direction="row" justifyContent="center" alignItems="baseline" gap={1}>
                                    <Typography variant="body2" sx={{ color: 'text.disabled', pb: 1, pt: 2 }}>
                                        {_t('mobile_comments_comment_deleted')}
                                    </Typography>
                                </Stack>
                            )}
                            <Stack
                                direction="row"
                                justifyContent="end"
                                alignItems="baseline"
                                gap={1}
                                sx={{
                                    ...(!comment.canDelete ? { pr: 6 } : {})
                                }}
                            >
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {comment.dateDisplayed}
                                </Typography>
                            </Stack>
                        </>
                    }
                    slotProps={{ secondary: { component: 'div' } }}
                />
            </ListItem>

            {showDivider && <Divider component="li" />}

            <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleCloseMenu}>
                <MenuItem onClick={handleDeleteComment}>
                    <ListItemIcon>
                        <DeleteOutlineIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>{_t('mobile_drafts_button_delete')}</ListItemText>
                </MenuItem>
            </Menu>

            <ConfirmDialog
                open={isPendingDelete}
                title={_t('mobile_comments_delete_button')}
                description={_t('mobile_follow_up_actions_comments_delete_modal_subtitle')}
                confirmLabel={_t('mobile_comments_delete_button')}
                cancelLabel={_t('mobile_action_sheet_cancel')}
                onConfirm={onConfirmDelete}
                onCancel={onCancelDelete}
                onClose={onCancelDelete}
                confirmProps={{ color: 'error' }}
            />
        </>
    );
};
